import { parseQuery } from '@calendly/http-tools'
import { createSlice } from '@reduxjs/toolkit'
import moment from 'moment-timezone'

import { formatTimestampAsString, getDefaultTimes } from './helpers'
import { timezoneSelector } from '../../selectors/current_user'


const initialState = {
  params: {
    past: {},
    future: {},
    availability: {},
    routing: {},
  },
}

const slice = createSlice({
  name: 'reporting',
  initialState,
  reducers: {
    setPastStart: (state, { payload }) => {
      state.params.past.start = payload
    },
    setPastEnd: (state, { payload }) => {
      state.params.past.end = payload
    },
    setFutureStart: (state, { payload }) => {
      state.params.future.start = payload
    },
    setFutureEnd: (state, { payload }) => {
      state.params.future.end = payload
    },
    setAvailabilityStart: (state, { payload }) => {
      state.params.availability.start = payload
    },
    setAvailabilityEnd: (state, { payload }) => {
      state.params.availability.end = payload
    },
    setRoutingStart: (state, { payload }) => {
      state.params.routing.start = payload
    },
    setRoutingEnd: (state, { payload }) => {
      state.params.routing.end = payload
    },
    setTimezone: (state, { payload }) => {
      state.params.timezone = payload
    },
    setUsersFilter: (state, { payload }) => {
      state.params.users = payload
    },
    setTeamsFilter: (state, { payload }) => {
      state.params.teams = payload
    },
    setGroupsFilter: (state, { payload }) => {
      state.params.groups = payload
    },
    setEventTypesFilter: (state, { payload }) => {
      state.params.eventTypes = payload
    },
  }
})

const {
  setAvailabilityEnd,
  setAvailabilityStart,
  setRoutingStart,
  setRoutingEnd,
  setEventTypesFilter,
  setFutureEnd,
  setFutureStart,
  setGroupsFilter,
  setPastEnd,
  setPastStart,
  setTeamsFilter,
  setTimezone,
  setUsersFilter,
} = slice.actions

export const setPastDateRange = ({ start, end }) => (dispatch) => {
  dispatch(setPastStart(start))
  dispatch(setPastEnd(end))
}

export const setFutureDateRange = ({ start, end }) => (dispatch) => {
  dispatch(setFutureStart(start))
  dispatch(setFutureEnd(end))
}

export const setAvailabilityDateRange = ({ start, end }) => (dispatch) => {
  dispatch(setAvailabilityStart(start))
  dispatch(setAvailabilityEnd(end))
}

export const setRoutingDateRange = ({ start, end }) => (dispatch) => {
  dispatch(setRoutingStart(start))
  dispatch(setRoutingEnd(end))
}

export const setFilters = ({ users = [], teams = [], groups = [], eventTypes = [] }) => (dispatch) => {
  dispatch(setUsersFilter(users))
  dispatch(setTeamsFilter(teams))
  dispatch(setGroupsFilter(groups))
  dispatch(setEventTypesFilter(eventTypes))
}

const getInitialFilters = ({
  queryParams,
  usersCollection,
  teamsCollection,
  groupsCollection,
  eventTypesCollection
}) => {
  const {
    users = [],
    teams = [],
    groups = [],
    event_types: eventTypes = []
  } = queryParams

  const collectItemsByUuids = (items, uuids) => {
    const foundItems = uuids.map((uuid) => items.find((it) => it.uuid === uuid))
    return foundItems.filter(Boolean)
  }

  const filters = {
    users: () => collectItemsByUuids(usersCollection, users),
    teams: () => collectItemsByUuids(teamsCollection, teams),
    groups: () => collectItemsByUuids(groupsCollection, groups),
    eventTypes: () => collectItemsByUuids(eventTypesCollection.collection, eventTypes),
  }

  const activeFilterKey = Object.keys(filters).find((filterKey) => (filters[filterKey]()).length > 0)

  if (!activeFilterKey) return {}

  const activeFilterValue = filters[activeFilterKey]()

  return { [activeFilterKey]: activeFilterValue }
}

export const init = ({
  usersCollection = [],
  teamsCollection = [],
  groupsCollection = [],
  eventTypesCollection = {}
} = {}) => (dispatch, getState) => {
  const state = getState()

  const timezone = timezoneSelector(state) || moment.tz.guess(true)

  const { start, end, ...queryParams } = parseQuery(window.location.search)
  const { past, future } = getDefaultTimes(timezone)

  const initialFilters = getInitialFilters({
    queryParams,
    usersCollection,
    teamsCollection,
    groupsCollection,
    eventTypesCollection
  })

  const formattedStart = formatTimestampAsString(start, timezone)
  const formattedEnd = formatTimestampAsString(end, timezone)

  dispatch(setPastDateRange((formattedStart && formattedEnd) ? { start: formattedStart, end: formattedEnd } : past))
  dispatch(setFutureDateRange((formattedStart && formattedEnd) ? { start: formattedStart, end: formattedEnd } : future))
  dispatch(setFilters(initialFilters))
  dispatch(setTimezone(timezone))
}

export const { reducer } = slice
